import { useEffect } from "react";
import { useConfig } from "@kaltura/mediaspace-shared-data-kms-config/useConfig";
import LocalIframe from "../local-iframe/LocalIframe";
import PreviewEmbedProps from "../PreviewEmbedProps";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";

/**
 * Preview Rapt Media embed in an iframe
 *
 * an iframe is used to load the relevant player lib without interfering
 * with the host page.
 *
 * Supports Rapt videos.
 */
export const PreviewEmbedRapt: React.FC<PreviewEmbedProps> = (props: PreviewEmbedProps) => {
    const {
        width = 432,
        height = 243,
        title,
        partnerId,
        uiConfId,
        entryId,
        ks,
        serviceUrl,
        cdnUrl = "https://cdnapisec.kaltura.com",
        startTime,
        endTime,
        loadThumbnailWithKs = false,
        onPlay,
    } = props;

    const {
        application: { cspNonce },
    } = useConfig();

    const playerLib = `${cdnUrl}/p/${partnerId}/embedPlaykitJs/uiconf_id/${uiConfId}`;

    // Base configuration for the player
    const config = {
        targetId: "kaltura_rapt_player",
        provider: { partnerId, uiConfId, ks, loadThumbnailWithKs },
        env: { serviceUrl, cdnUrl },
        playback: { startTime, endTime },
    };

    // setup script for Rapt
    const script = `
        try {
            var kalturaPlayer = PathKalturaPlayer.setup(${JSON.stringify(config)});
            kalturaPlayer.loadMedia({playlistId:"${entryId}"});
            kalturaPlayer.addEventListener(kalturaPlayer.Event.UI.USER_CLICKED_PLAY, event => {
                parent.postMessage({id: "${uiConfId}"}, '*');
            });
        } catch (e) {
            console.error(e.message)
        };`;

    const content = `
        <div class="chromatic-ignore">
            <div id="kaltura_rapt_player"></div>
            <script src="${playerLib}"></script>
            <script nonce="${cspNonce}">${script}</script>
        </div>`;
    const handleOnPlay = useEventHandler(onPlay);

    useEffect(() => {
        const handlePlay = (ev: any) => {
            if (ev.data && ev.data.id === uiConfId) {
                handleOnPlay?.();
                window.removeEventListener("message", handlePlay);
            }
        };
        window.addEventListener("message", handlePlay);

        return () => {
            window.removeEventListener("message", handlePlay);
        };
    }, [handleOnPlay, uiConfId]);

    return <LocalIframe content={content} title={title} width={width} height={height} />;
};

export default PreviewEmbedRapt;
